import { QueryClient } from '@tanstack/react-query';
import createClient from 'openapi-fetch';
import type { paths } from '../generated/api.ts';
import { parseRecordObject } from '../shared/content.ts';
import type { RenderContentObject } from '../shared/content.ts';
import { snapshotSchema } from '../shared/contracts.ts';

export const api = createClient<paths>({ credentials: 'same-origin' });
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (attempt, error) => attempt < 2 && (!(error instanceof ApiError) || error.status >= 500),
      staleTime: 15_000,
    },
    mutations: { retry: false },
  },
});

export class ApiError extends Error {
  readonly status: number;
  readonly detail: unknown;
  constructor(status: number, detail: unknown) {
    super(
      detail && typeof detail === 'object' && 'detail' in detail && typeof detail.detail === 'string'
        ? detail.detail
        : detail && typeof detail === 'object' && 'message' in detail && typeof detail.message === 'string'
          ? detail.message
          : `Request failed (${status}).`,
    );
    this.status = status;
    this.detail = detail;
  }
}
export function isActivationRequired(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 402 &&
    Boolean(
      error.detail &&
      typeof error.detail === 'object' &&
      'code' in error.detail &&
      error.detail.code === 'activation_required',
    )
  );
}
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error || !result.response.ok) throw new ApiError(result.response.status, result.error);
  if (result.data === undefined) throw new Error('The server returned an empty response.');
  return result.data;
}
export const meQuery = { queryKey: ['me'], queryFn: async () => unwrap(await api.GET('/api/me')) };
export const accountsQuery = {
  queryKey: ['accounts'],
  queryFn: async () => unwrap(await api.GET('/api/accounts')),
};
export const sitesQuery = { queryKey: ['sites'], queryFn: async () => unwrap(await api.GET('/api/sites')) };
export const invalidateSite = (siteId: string) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: ['site', siteId] }),
    queryClient.invalidateQueries({ queryKey: ['checks', siteId] }),
    queryClient.invalidateQueries({ queryKey: ['sites'] }),
  ]);
export const siteSettingsQuery = (siteId: string, version: 'Draft' | 'Published' = 'Draft') => ({
  queryKey: ['site', siteId, 'settings', version],
  queryFn: async () => {
    const settings = parseRecordObject(
      unwrap(
        await api.GET('/api/sites/{siteId}/settings', { params: { path: { siteId }, query: { version } } }),
      ),
    );
    if (settings.kind !== 'Settings') throw new Error('Expected site settings.');
    return settings;
  },
});
export const pageQuery = (siteId: string, objectId: string) => ({
  queryKey: ['site', siteId, 'page', objectId],
  queryFn: async () =>
    unwrap(
      await api.GET('/api/pages/{pageId}', {
        params: { path: { pageId: objectId }, query: { version: 'Draft' } },
      }),
    ),
});
async function allPageSummaries(siteId: string, archived: 'true' | 'false') {
  const items = [];
  for (let page = 1; ; page++) {
    const result = unwrap(
      await api.GET('/api/sites/{siteId}/pages', {
        params: { path: { siteId }, query: { archived, page, pageSize: 100 } },
      }),
    );
    items.push(...result.items);
    if (result.page * result.pageSize >= result.total) return items;
  }
}
export const siteQuery = (siteId: string, { includePages = true } = {}) => ({
  queryKey: ['site', siteId, { includePages }],
  queryFn: async () => {
    const [settings, ...groups] = await Promise.all([
      queryClient.fetchQuery(siteSettingsQuery(siteId)),
      ...(['false', 'true'] as const).map(async (archived) => {
        const params = { path: { siteId }, query: { archived } };
        const [pages, forms, assets] = await Promise.all([
          includePages ? allPageSummaries(siteId, archived) : [],
          api.GET('/api/sites/{siteId}/forms', { params }).then(unwrap),
          api.GET('/api/sites/{siteId}/assets', { params }).then(unwrap),
        ]);
        return [
          // Listings omit page bodies; the editor loads the full record on demand.
          ...pages.map(({ collection, missingLocales: _missingLocales, publishedAt, ...p }) =>
            parseRecordObject({
              ...p,
              body: [],
              metaTitle: p.title,
              metaDescription: '',
              additionalObjects: [],
              noIndex: false,
              ...(p.kind === 'Post'
                ? { publishedAt: publishedAt ?? p.createdAt, collection: collection ?? 'blog' }
                : {}),
            }),
          ),
          ...forms,
          ...assets.map(parseRecordObject),
        ] as RenderContentObject[];
      }),
    ]);
    return snapshotSchema.parse({ siteId, objects: [settings, ...groups.flat()] });
  },
});
